import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, ALL_LINKS } from "@/lib/data";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-ink/70 border-b border-line">
        <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between" aria-label="Primary">
          <Link to="/" data-testid="nav-brand" className="flex items-baseline gap-3 group">
            <span className="font-display font-extrabold text-xl tracking-tight text-white">T-EMRP</span>
            <span className="hidden md:block font-mono text-[9px] uppercase tracking-[0.2em] text-mist group-hover:text-slate-300 transition-colors">
              Tactical Emergency Medical Readiness Program
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <ul className="hidden lg:flex items-center gap-6">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                        isActive ? "text-signal" : "text-slate-300 hover:text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 transition-colors"
            >
              Request Information <ArrowUpRight size={13} />
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              className="text-white p-2 hover:text-signal transition-colors"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-overlay"
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-2xl overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 grid lg:grid-cols-[1fr_320px] gap-16">
              <ul>
                {ALL_LINKS.map((l, i) => (
                  <motion.li
                    key={l.to}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.04, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="border-b border-line"
                  >
                    <Link
                      to={l.to}
                      data-testid={`overlay-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="group flex items-baseline gap-6 py-4"
                    >
                      <span className="font-mono text-xs text-signal">{l.num}</span>
                      <span className={`font-display font-bold text-3xl md:text-5xl tracking-tight transition-colors ${pathname === l.to ? "text-signal" : "text-white group-hover:text-signal"}`}>
                        {l.label}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="hidden lg:block pt-4"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-6">Status</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-10">
                  T-EMRP is a proposed pilot framework in pre-pilot status. It does not replace existing clinical standards, local protocols, medical direction, or incident-command doctrine.
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-4">Legal</p>
                <div className="flex flex-col gap-2">
                  <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors">Terms of Use</Link>
                  <Link to="/accessibility" className="text-sm text-slate-400 hover:text-white transition-colors">Accessibility</Link>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
